import gsap from 'gsap'
import { DrawSVGPlugin } from 'gsap/all'
import { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useGSAP } from '@gsap/react'

import { InactivityContent, InactivityOverlay, InactivityTimer, InactivityTimerContent, InactivityTimerSvg } from './Inactivity.styles'

interface InactivityProps {
  durationSeconds: number
  onContinue: () => void
  onTimeout: () => void
}

const CIRCLE_RADIUS = 180
const STROKE_WIDTH = 8

gsap.registerPlugin(useGSAP, DrawSVGPlugin)

const Inactivity = ({ durationSeconds, onContinue, onTimeout }: InactivityProps) => {
  const { t } = useTranslation()
  const overlayRef = useRef<HTMLButtonElement>(null)
  const progressCircleRef = useRef<SVGCircleElement>(null)
  const [remainingSeconds, setRemainingSeconds] = useState(durationSeconds)

  useGSAP(
    () => {
      const progressCircle = progressCircleRef.current
      if (!progressCircle) return

      if (durationSeconds <= 0) {
        setRemainingSeconds(0)
        onTimeout()
        return
      }

      const counter = { ms: durationSeconds * 1000 }

      setRemainingSeconds(durationSeconds)
      gsap.set(progressCircle, { drawSVG: '0% 100%' })

      const timeline = gsap.timeline({ onComplete: onTimeout })

      timeline.to(progressCircle, { drawSVG: '100% 100%', duration: durationSeconds, ease: 'none' }, 0)
      timeline.to(
        counter,
        {
          ms: 0,
          duration: durationSeconds,
          ease: 'none',
          onUpdate: () => {
            const nextSeconds = Math.max(0, Math.ceil(counter.ms / 1000))
            setRemainingSeconds(current => (current === nextSeconds ? current : nextSeconds))
          }
        },
        0
      )

      return () => {
        timeline.kill()
      }
    },
    { scope: overlayRef, dependencies: [durationSeconds, onTimeout], revertOnUpdate: true }
  )

  return (
    <InactivityOverlay ref={overlayRef} type="button" onClick={onContinue}>
      <InactivityContent>
        <InactivityTimer>
          <InactivityTimerSvg viewBox="0 0 400 400" aria-hidden="true">
            <circle cx="200" cy="200" r={CIRCLE_RADIUS} fill="none" strokeWidth={STROKE_WIDTH} />
            <circle
              ref={progressCircleRef}
              cx="200"
              cy="200"
              r={CIRCLE_RADIUS}
              fill="none"
              strokeWidth={STROKE_WIDTH}
              strokeLinecap="round"
            />
          </InactivityTimerSvg>
          <InactivityTimerContent>
            <p>{t('inactivity.resettingLabel')}</p>
            <strong>{remainingSeconds}s</strong>
          </InactivityTimerContent>
        </InactivityTimer>
        <p>{t('inactivity.caption')}</p>
      </InactivityContent>
    </InactivityOverlay>
  )
}

export default Inactivity
